import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  MONDAY_FIRST_WEEKDAY_KEYS,
  formatMonthYearLabel,
  getMondayWeekdayLabelsLocalized,
} from '../utils/calendarPickerLabels';
import { formatDate, formatLongDateLabel } from '../utils/localization';
import {
  addMonths,
  buildMonthCalendarDays,
  parseLocalDateKey,
  startOfMonth,
  toDateKey,
} from '../utils/calendarPickerUtils';
import styles from './LocalizedDateInput.module.css';

type LocalizedDateDisplayFormat = 'long' | 'medium' | 'input';

export interface LocalizedDateInputProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value' | 'onChange' | 'type'> {
  value: string;
  onValueChange?: (value: string) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  displayFormat?: LocalizedDateDisplayFormat;
  emptyLabel?: string;
  displayClassName?: string;
  fillWidth?: boolean;
}

const formatDisplayValue = (value: string, displayFormat: LocalizedDateDisplayFormat): string => {
  if (!value) {
    return '';
  }

  if (displayFormat === 'input') {
    return value;
  }

  if (displayFormat === 'long') {
    return formatLongDateLabel(value);
  }

  return formatDate(value, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const POPOVER_WIDTH = 320;
const POPOVER_GAP = 8;
const POPOVER_MIN_SPACE_BELOW = 280;

const buildPopoverStyle = (trigger: HTMLElement): React.CSSProperties => {
  const rect = trigger.getBoundingClientRect();
  const width = Math.min(POPOVER_WIDTH, window.innerWidth - 32);
  const left = Math.min(
    Math.max(16, rect.left),
    window.innerWidth - width - 16,
  );
  const spaceBelow = window.innerHeight - rect.bottom - POPOVER_GAP;
  const spaceAbove = rect.top - POPOVER_GAP;
  const openUpward = spaceBelow < POPOVER_MIN_SPACE_BELOW && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    top: openUpward ? undefined : rect.bottom + POPOVER_GAP,
    bottom: openUpward ? window.innerHeight - rect.top + POPOVER_GAP : undefined,
    left,
    width,
    zIndex: 1200,
  };
};

const popoverStylesEqual = (left: React.CSSProperties, right: React.CSSProperties): boolean =>
  left.top === right.top
  && left.bottom === right.bottom
  && left.left === right.left
  && left.width === right.width;

export const LocalizedDateInput: React.FC<LocalizedDateInputProps> = ({
  value,
  onValueChange,
  onChange,
  displayFormat = 'long',
  emptyLabel,
  className,
  displayClassName,
  fillWidth = false,
  disabled,
  onClick,
  ...rest
}) => {
  const { t } = useTranslation(['calendar', 'common']);
  const translateCalendarDateLabel = React.useCallback(
    (key: string) => t(key, { ns: 'calendar' }),
    [t],
  );
  const resolvedEmptyLabel = emptyLabel ?? t('selectDate', { ns: 'calendar' });
  const displayValue = value ? formatDisplayValue(value, displayFormat) : resolvedEmptyLabel;

  const pickerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [popoverStyle, setPopoverStyle] = React.useState<React.CSSProperties | null>(null);

  const selectedDate = React.useMemo(() => parseLocalDateKey(value), [value]);
  const [visibleMonth, setVisibleMonth] = React.useState(() => startOfMonth(selectedDate ?? new Date()));

  const weekdayLabels = React.useMemo(
    () => getMondayWeekdayLabelsLocalized(translateCalendarDateLabel),
    [translateCalendarDateLabel],
  );
  const calendarDays = React.useMemo(
    () => buildMonthCalendarDays(visibleMonth, value || undefined),
    [value, visibleMonth],
  );
  const monthLabel = React.useMemo(
    () => formatMonthYearLabel(visibleMonth, translateCalendarDateLabel),
    [translateCalendarDateLabel, visibleMonth],
  );

  const updatePopoverPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    const nextStyle = buildPopoverStyle(trigger);
    setPopoverStyle(current => (current && popoverStylesEqual(current, nextStyle) ? current : nextStyle));
  }, []);

  const closePicker = React.useCallback(() => {
    setIsOpen(false);
    setPopoverStyle(null);
  }, []);

  const openPicker = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }

    setVisibleMonth(startOfMonth(selectedDate ?? new Date()));
    setPopoverStyle(buildPopoverStyle(trigger));
    setIsOpen(true);
  }, [selectedDate]);

  React.useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePopoverPosition();
  }, [isOpen, updatePopoverPosition]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (pickerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }

      closePicker();
    };

    const handleReposition = () => updatePopoverPosition();

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [closePicker, isOpen, updatePopoverPosition]);

  const emitValueChange = (nextValue: string) => {
    onValueChange?.(nextValue);
    onChange?.({
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const selectCalendarDay = (date: Date) => {
    emitValueChange(toDateKey(date));
    closePicker();
  };

  const toggleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (disabled) {
      return;
    }

    event.preventDefault();
    if (isOpen) {
      closePicker();
      return;
    }

    openPicker();
  };

  const popover = isOpen && popoverStyle ? (
    <div
      ref={popoverRef}
      className={styles.popover}
      style={popoverStyle}
      role="dialog"
      aria-label={displayValue}
    >
      <div className={styles.monthHeader}>
        <button
          type="button"
          className={styles.monthNavButton}
          onClick={() => setVisibleMonth((current) => addMonths(current, -1))}
          aria-label={translateCalendarDateLabel('previousMonth')}
        >
          ‹
        </button>
        <span className={styles.monthLabel}>{monthLabel}</span>
        <button
          type="button"
          className={styles.monthNavButton}
          onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
          aria-label={translateCalendarDateLabel('nextMonth')}
        >
          ›
        </button>
      </div>

      <div className={styles.weekdayRow}>
        {weekdayLabels.map((label, index) => (
          <span key={MONDAY_FIRST_WEEKDAY_KEYS[index]} className={styles.weekdayLabel}>{label}</span>
        ))}
      </div>

      <div className={styles.dayGrid}>
        {calendarDays.map((day) => (
          <button
            key={day.dayKey}
            type="button"
            className={[
              styles.dayButton,
              !day.isCurrentMonth ? styles.dayOutsideMonth : '',
              day.isSelected ? styles.daySelected : '',
              day.isToday ? styles.dayToday : '',
            ].filter(Boolean).join(' ')}
            onClick={() => selectCalendarDay(day.date)}
          >
            {day.dayNumber}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div
      className={[
        styles.picker,
        fillWidth ? styles.pickerFill : '',
      ].filter(Boolean).join(' ')}
      ref={pickerRef}
    >
      <button
        {...rest}
        ref={triggerRef}
        type="button"
        className={[
          styles.trigger,
          fillWidth ? styles.triggerFill : '',
          isOpen ? styles.triggerOpen : '',
          !value ? styles.triggerEmpty : '',
          className,
          displayClassName,
        ].filter(Boolean).join(' ')}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={toggleOpen}
      >
        {displayValue}
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
};
